import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Plus, ArrowRight, ChevronRight, FileText, AlertCircle, Trash2 } from 'lucide-react';
import { useOrg } from '../../hooks/useOrg';
import { listAssessments, deleteAssessment } from '../../lib/assessments/storage';
import { MOCA } from '../../lib/assessments/moca';

// Catalog of assessments we can administer. Today it's just MoCA; the data
// shape is set up so adding GCS / RBANS / SLUMS later is a single entry.
const ASSESSMENTS = [
  {
    key: 'moca',
    name: MOCA.name,
    short: 'MoCA',
    version: MOCA.version,
    duration: `${MOCA.duration_minutes} min`,
    summary: '30-point cognitive screening — visuospatial, naming, memory, attention, language, abstraction, delayed recall, and orientation.',
    domains: ['Visuospatial', 'Naming', 'Memory', 'Attention', 'Language', 'Abstraction', 'Recall', 'Orientation'],
    attribution: MOCA.attribution,
    start: '/app/tbi-assessments/moca/new',
    available: true,
  },
  {
    key: 'slums',
    name: 'Saint Louis University Mental Status (SLUMS)',
    short: 'SLUMS',
    version: '',
    duration: '7 min',
    summary: '30-point alternative cognitive screen — particularly sensitive to mild neurocognitive disorder.',
    domains: ['Memory', 'Attention', 'Executive'],
    attribution: 'Saint Louis University',
    start: null,
    available: false,
  },
  {
    key: 'gcs',
    name: 'Glasgow Coma Scale (GCS)',
    short: 'GCS',
    version: '',
    duration: '2 min',
    summary: 'Acute consciousness assessment scoring eye-opening, verbal, and motor responses.',
    domains: ['Acute injury'],
    attribution: 'Public domain',
    start: null,
    available: false,
  },
];

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function ScoreChip({ result }) {
  if (!result) return null;
  const ok = result.isNormal;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] text-[11px] font-semibold ${
        ok ? 'bg-success-soft text-success' : 'bg-warning/10 text-warning'
      }`}
    >
      <span className="font-mono">{result.total}/{result.max}</span>
      <span className="opacity-60">·</span>
      <span>{ok ? 'Normal' : 'Below threshold'}</span>
    </div>
  );
}

export default function TbiAssessmentsPage() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [tick, setTick] = useState(0);

  // `tick` is the cache-buster after a delete — referencing it inside the
  // memo is intentional, so we list it as a dep.
  const records = useMemo(
    () => (currentOrg?.id ? listAssessments(currentOrg.id) : []),
    [currentOrg?.id, tick]
  );

  useEffect(() => { setTick(t => t + 1); }, [currentOrg?.id]);

  const handleDelete = (id) => {
    if (!window.confirm('Delete this assessment? This cannot be undone.')) return;
    deleteAssessment(id);
    setTick(t => t + 1);
  };

  return (
    <main className="flex-1 bg-primary">
      <div className="max-w-[1100px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Brain size={18} strokeWidth={2.2} className="text-accent-light" />
              <span className="text-[11px] uppercase tracking-wide text-accent-light font-semibold">
                TBI Assessments
              </span>
            </div>
            <h1 className="text-[24px] font-bold text-primary-c">Cognitive screening</h1>
          </div>
          <button
            onClick={() => navigate('/app/tbi-assessments/moca/new')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:opacity-95 transition-all"
          >
            <Plus size={14} strokeWidth={2.5} /> Start MoCA
          </button>
        </div>
        <p className="text-[13px] text-muted-c max-w-[640px] mb-6">
          Run validated cognitive screens for patients with suspected traumatic brain
          injury — concussion, MVA, slip-and-fall. Scores are saved to the case record.
        </p>

        {/* Catalog */}
        <section className="mb-8">
          <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold mb-3">
            Available assessments
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ASSESSMENTS.map(a => (
              <div
                key={a.key}
                className={`rounded-[14px] border border-theme overflow-hidden bg-elevated ${
                  a.available ? '' : 'opacity-60'
                }`}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-[40px] h-[40px] rounded-[10px] bg-accent-glow flex items-center justify-center text-accent-light">
                      <Brain size={18} strokeWidth={2.2} />
                    </div>
                    {a.available ? (
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-accent-light bg-accent-glow px-2 py-0.5 rounded-[6px]">
                        Ready
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-c bg-tertiary px-2 py-0.5 rounded-[6px]">
                        Coming soon
                      </span>
                    )}
                  </div>

                  <div className="text-[14px] font-bold text-primary-c mb-1">{a.short}</div>
                  <div className="text-[11px] text-muted-c mb-3">{a.name}{a.version ? ` · v${a.version}` : ''}</div>

                  <p className="text-[12px] text-secondary-c leading-relaxed mb-4 min-h-[3.5em]">
                    {a.summary}
                  </p>

                  <div className="flex items-center justify-between text-[11px] text-muted-c mb-4">
                    <span>~{a.duration}</span>
                    <span className="truncate ml-2">{a.attribution}</span>
                  </div>

                  <button
                    disabled={!a.available}
                    onClick={() => a.start && navigate(a.start)}
                    className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-[10px] text-[12px] font-semibold transition-all ${
                      a.available
                        ? 'bg-accent-glow text-accent-light hover:bg-hover-surface border border-strong'
                        : 'bg-tertiary text-muted-c cursor-not-allowed border border-theme'
                    }`}
                  >
                    {a.available ? <>Start assessment <ArrowRight size={13} strokeWidth={2.5} /></> : 'Not available yet'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* History */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold">
              Recent assessments
            </div>
            <div className="text-[11px] text-muted-c">
              {records.length} record{records.length === 1 ? '' : 's'}
            </div>
          </div>

          {records.length === 0 ? (
            <div className="rounded-[14px] border border-dashed border-theme bg-secondary p-10 text-center">
              <FileText size={20} strokeWidth={1.8} className="text-muted-c mx-auto mb-3" />
              <div className="text-[13px] font-semibold text-primary-c mb-1">
                No assessments yet
              </div>
              <div className="text-[12px] text-muted-c mb-4 max-w-[420px] mx-auto leading-relaxed">
                Start your first cognitive screen and the results will appear here.
                Each assessment is auto-saved as you progress.
              </div>
              <button
                onClick={() => navigate('/app/tbi-assessments/moca/new')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all"
              >
                <Plus size={13} strokeWidth={2.5} /> Start MoCA
              </button>
            </div>
          ) : (
            <div className="rounded-[14px] border border-theme overflow-hidden bg-elevated divide-y divide-theme">
              {records.map(r => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/app/tbi-assessments/${r.id}`)}
                  className="w-full text-left px-5 py-4 hover:bg-hover-surface transition-all flex items-center gap-4 group"
                >
                  <div className="w-[36px] h-[36px] rounded-[10px] bg-accent-glow text-accent-light flex items-center justify-center flex-shrink-0">
                    <Brain size={15} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[13px] font-semibold text-primary-c truncate">
                        {r.patient_name}
                      </span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-c">
                        {r.type === 'moca' ? 'MoCA' : r.type}
                      </span>
                    </div>
                    <div className="text-[11px] text-muted-c">{fmtDate(r.completed_at)}</div>
                  </div>
                  <ScoreChip result={r.result} />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                    className="p-1.5 rounded-[8px] text-muted-c opacity-0 group-hover:opacity-100 hover:text-danger hover:bg-danger/10 transition-all"
                    aria-label="Delete"
                  >
                    <Trash2 size={13} strokeWidth={2} />
                  </button>
                  <ChevronRight size={14} strokeWidth={2.2} className="text-muted-c flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Licensing footnote */}
        <div className="mt-8 flex items-start gap-2 text-[11px] text-muted-c max-w-[680px]">
          <AlertCircle size={12} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
          <p>
            MoCA is © Z. Nasreddine, MD (mocatest.org). The platform implementation
            shown here is for clinical workflow design review. Production use requires
            provider certification and a licensing agreement with the MoCA Clinic &
            Institute.
          </p>
        </div>
      </div>
    </main>
  );
}
